import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ReplaySubject, Subject, take } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import * as WebSocketAuthenticate from '../../server/routes/web-socket/authenticate';
import * as WebSocketInitiateAuthenticate from '../../server/routes/web-socket/initiate-authenticate';
import * as WebSocketUrl from '../../server/routes/web-socket/url';
import { generateId } from '../../server/services/utils.service';
import { type WebSocketPushData } from '../../server/services/web-socket.service';
import { AuthUserService } from './auth-user.service';

@Injectable()
export class WebSocketService {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);

  private appInstanceId: string;
  private webSocket?: WebSocketSubject<WebSocketPushData> | undefined;
  private webSocketUrlReceived = false;
  private webSocketUrl = new ReplaySubject<string>(1);
  private pushSubject = new Subject<WebSocketPushData>();
  pushObservable = this.pushSubject.asObservable();

  private onLineStore = true;
  private webSocketConnectedStore = false;
  private connectionActiveSubject = new ReplaySubject<
    'onLine' | 'offLine' | 'noWebSocket'
  >();
  connectionActive = this.connectionActiveSubject.asObservable();

  connectionId?: string;

  refreshId?: string;

  constructor() {
    this.appInstanceId = this.authUserService.appInstanceId;

    // Check every 10 seconds if the websocket is connected, otherwise reconnect
    setInterval(() => {
      if (!this.webSocket) this.initiateWebSocket();
    }, 10000);

    // Refresh connection (AWS connections ends after 10 minutes without refresh, can be extended by refreshing up to 2 hours)
    setInterval(() => {
      const refreshId = generateId();
      this.refreshId = refreshId;
      this.webSocket?.next({
        service: 'webSocket',
        action: 'refresh',
        data: { refreshId },
      });
      // Wait for websocket to come back, if it doesn't disconnect and reconnect
      setTimeout(() => {
        if (this.refreshId && this.refreshId === refreshId) {
          if (this.webSocket) this.disconnect();
          else this.initiateWebSocket();
        }
      }, 10000);
    }, 60000);

    // Set connection
    this.connectionActiveSubject.next('onLine'); // Starts as online, does not change until 1 second later, hopefully enough time to get the websocket result through
    this.setOnLine();
    setInterval(() => this.setOnLine(), 1000);

    // Pre-load url for speed
    this.getWebSocketUrl();

    // Initiate websocket connection any time a new login or register is detected
    this.authUserService.isLoggedIn.subscribe((isLoggedIn) => {
      if (isLoggedIn) this.initiateWebSocket();
    });
  }

  private setOnLine() {
    if (navigator.onLine !== this.onLineStore) {
      this.onLineStore = navigator.onLine;
      this.updateConnectionActiveSubject();
    }
  }

  private setWebSocketConnectionState(state: boolean) {
    let updateConnection = false;
    if (navigator.onLine !== this.onLineStore) {
      this.onLineStore = navigator.onLine;
      updateConnection = true;
    }

    if (this.webSocketConnectedStore !== state) {
      this.webSocketConnectedStore = state;
      updateConnection = true;
    }

    if (updateConnection) this.updateConnectionActiveSubject();
  }

  private updateConnectionActiveSubject() {
    if (this.onLineStore)
      if (this.webSocketConnectedStore)
        this.connectionActiveSubject.next('onLine');
      else this.connectionActiveSubject.next('noWebSocket');
    else this.connectionActiveSubject.next('offLine');
  }

  private initiateWebSocket() {
    if (!this.webSocket) {
      this.getWebSocketUrl()
        .pipe(take(1))
        .subscribe({
          next: (url) => {
            if (this.webSocket) return;

            this.webSocket = webSocket(url);

            // WebSocket will not start until subscribe is called.
            this.webSocket.subscribe({
              next: (socketResult) => {
                this.setWebSocketConnectionState(true);

                if (socketResult.service === 'webSocket') {
                  if (socketResult.action === 'refresh')
                    if (this.refreshId === socketResult.data.refreshId)
                      this.refreshId = undefined;
                  if (socketResult.data.connectionId)
                    this.initiateAuthenticate(socketResult.data.connectionId);
                  if (socketResult.action === 'authenticate')
                    this.finalizeAuthenticate({
                      token: socketResult.data.token,
                      connectionId: socketResult.data.connectionId,
                    });
                } else {
                  this.pushSubject.next(socketResult);
                }
              },
              error: (err) => {
                if (err.code !== 1006) {
                  // this is a client WS error, try to re-open
                  console.error(err);
                }

                if (this.webSocket) this.disconnect();
                else this.initiateWebSocket();
              },
              complete: () => {
                // This function is called when the socket is closed. If it closes,
                // attempt to reopen. We should limit attempts to restart the service.
                this.webSocket = undefined;
                this.setWebSocketConnectionState(false);
                this.initiateWebSocket();
              },
            });

            this.webSocket.next({
              service: 'webSocket',
              action: 'getConnectionId',
            });
          },
          error: (err) => {
            throw err;
          },
        });
    }
  }

  private getWebSocketUrl() {
    if (!this.webSocketUrlReceived)
      this.http.get<WebSocketUrl.ResBody>('/api/web-socket/url').subscribe({
        next: ({ url }) => {
          this.webSocketUrl.next(url);
          this.webSocketUrlReceived = true;
        },
        error: (err) => {
          console.error(err);
        },
      });
    return this.webSocketUrl;
  }

  private initiateAuthenticate(connectionId: string) {
    if (this.connectionId === connectionId) return;
    this.connectionId = connectionId;
    const req: WebSocketInitiateAuthenticate.ReqBody = {
      connectionId,
      appInstanceId: this.appInstanceId,
    };
    this.http
      .post<WebSocketInitiateAuthenticate.ResBody>(
        '/api/web-socket/initiate-authenticate',
        req
      )
      .subscribe({
        next: () => {
          //
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  private finalizeAuthenticate({
    token,
    connectionId,
  }: {
    token: string;
    connectionId: string;
  }) {
    this.connectionId = connectionId;
    const req: WebSocketAuthenticate.ReqBody = { token, connectionId };
    this.http
      .post<WebSocketAuthenticate.ResBody>('/api/web-socket/authenticate', req)
      .subscribe({
        next: () => {
          //
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  private disconnect(): void {
    this.setWebSocketConnectionState(false);
    this.webSocket?.complete();
    this.webSocket = undefined;
  }
}
